/*
  Waken cart complements - behaviour for <waken-complements>, rendered by
  snippets/waken-cart-complements.liquid on the cart page and in the cart drawer.

  Loaded from layout/theme.liquid on every page. It used to be an inline
  <script> inside the snippet, which only ran when the page was served with
  items already in the basket. For anyone who arrived with an empty basket the
  drawer's markup came in later by section morph after their first add-to-cart,
  and browsers never execute scripts inside morphed HTML, so the "Add one for
  free delivery" buttons were dead for exactly the shoppers they were built for.
  Moved here 22 Sep 2026.

  Add buttons are handled by delegation on the element, so buttons that arrive
  by morph need no rewiring and can never be wired twice.
*/
(() => {
  if (customElements.get('waken-complements')) return;

  class WakenComplements extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick);
      // Horizon morphs the cart section in place after every change, so this
      // element usually survives with new data-* attributes but an empty list.
      this.onCartUpdate = () => setTimeout(() => this.load(), 60);
      document.addEventListener('cart:update', this.onCartUpdate);
      this.load();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('cart:update', this.onCartUpdate);
    }

    onClick = (event) => {
      const button = event.target.closest('.waken-complements__add');
      if (button && this.contains(button) && !button.disabled) this.add(button);
    };

    async load() {
      // Gap mode is server-rendered; the delegated click handler covers it.
      if (this.dataset.mode === 'gap') return;
      const pid = this.dataset.productId;
      if (!pid) { this.hidden = true; return; }
      const inCart = new Set((this.dataset.inCart || '').split(',').filter(Boolean));
      try {
        const res = await fetch(`${this.dataset.url}.json?product_id=${pid}&intent=complementary&limit=4`);
        if (!res.ok) return;
        const data = await res.json();
        const picks = (data.products || []).filter(p => !inCart.has(String(p.id)) && p.available).slice(0, 2);
        if (!picks.length) { this.hidden = true; return; }
        const list = this.querySelector('.waken-complements__list');
        list.innerHTML = picks.map(p => {
          const v = (p.variants || []).find(x => x.available) || p.variants[0];
          const img = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_120x$1$2') : '';
          const price = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(p.price / 100).replace('.00', '');
          const name = p.title.replace(/^Waken /, '');
          return `<li class="waken-complements__item">
            <a class="waken-complements__shot" href="${p.url}">${img ? `<img src="${img}" alt="" width="56" height="56" loading="lazy">` : ''}</a>
            <div class="waken-complements__body"><a href="${p.url}">${name}</a><span>${price}</span></div>
            <button type="button" class="waken-complements__add" data-variant="${v.id}" aria-label="Add ${name} to basket for ${price}">Add</button>
          </li>`;
        }).join('');
        this.hidden = false;
      } catch (e) { /* recommendations are optional; stay hidden */ }
    }

    async add(button) {
      button.disabled = true;
      const host = this.closest('cart-items-component');
      const sectionId = host ? host.dataset.sectionId : '';
      try {
        const res = await fetch(`${this.dataset.addUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(button.dataset.variant), quantity: 1 }], sections: sectionId, sections_url: window.location.pathname })
        });
        if (!res.ok) throw new Error('add failed');
        const added = await res.json();
        const cart = await fetch(`${this.dataset.cartUrl}.js`).then(r => r.json());
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'waken-complements', data: { source: 'waken-complements', itemCount: cart.item_count, variantId: String(button.dataset.variant), sections: added.sections || {} } }
        }));
      } catch (e) {
        window.location.reload();
      }
    }
  }

  customElements.define('waken-complements', WakenComplements);
})();
